import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useOrganization } from '@/hooks/useOrganization';
import { captureError } from '@/lib/sentry';
import {
  listMensagens,
  enviarMensagem,
  listInboxPreviews,
  type MensagemInsert,
  type Mensagem,
} from '@/services/mensagens.service';
import { buildOptimisticMensagem, buildOptimisticMidia } from '@/lib/optimistic-mensagem';
import {
  sendWhatsAppMessage,
  subscribeToMensagensChanges,
  subscribeToInboxChanges,
  unsubscribeMensagensChannel,
  iniciarConversa,
  uploadOutboundMedia,
  sendWhatsAppMedia,
  sendReaction,
  type IniciarConversaParams,
  type IniciarConversaResult,
} from '@/services/whatsapp.service';

export function useMensagens(clienteId: string | undefined) {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();

  const query = useQuery({
    queryKey: ['mensagens', organizationId, clienteId],
    queryFn: () => (clienteId ? listMensagens(clienteId) : Promise.resolve([])),
    enabled: !!clienteId,
  });

  // Realtime: subscribe a INSERT + UPDATE em mensagens; invalida cache para refetch.
  // Service helper isola supabase.channel — testes do hook não tocam Supabase real.
  useEffect(() => {
    if (!clienteId) return;
    const channel = subscribeToMensagensChanges(clienteId, () => {
      void queryClient.invalidateQueries({
        queryKey: ['mensagens', organizationId, clienteId],
      });
    });
    return () => {
      unsubscribeMensagensChannel(channel);
    };
  }, [clienteId, organizationId, queryClient]);

  return query;
}

/**
 * Mantém o inbox vivo: invalida a lista de clientes (re-ordena por
 * ultima_interacao e traz conversas novas) e as prévias/contagens de não-lida
 * a cada mensagem nova da org. Montar uma vez no Painel.
 */
export function useRealtimeInbox(orgId: string | undefined): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!orgId) return;
    const channel = subscribeToInboxChanges(orgId, () => {
      void queryClient.invalidateQueries({ queryKey: ['clientes'] });
      void queryClient.invalidateQueries({ queryKey: ['inbox-previews'] });
    });
    return () => {
      unsubscribeMensagensChannel(channel);
    };
  }, [orgId, queryClient]);
}

/**
 * Batch-fetch the latest message preview + unread count for a list of clientes.
 * Used by the Painel inbox to show the conversation snippet next to each row.
 */
export function useInboxPreviews(clienteIds: string[] | undefined) {
  const { organizationId } = useOrganization();
  // Sort keys so the cache key is stable regardless of input order.
  const sorted = (clienteIds ?? []).slice().sort();
  return useQuery({
    queryKey: ['inbox-previews', organizationId, sorted],
    queryFn: () => listInboxPreviews(sorted),
    enabled: sorted.length > 0,
    staleTime: 30_000,
  });
}

export function useEnviarMensagem() {
  const queryClient = useQueryClient();
  const { requireOrgId } = useOrganization();

  return useMutation({
    mutationFn: async (dados: MensagemInsert): Promise<Mensagem> => {
      // Branch: WhatsApp via edge function (persists optimistically server-side).
      // The edge function handles the INSERT into mensagens itself with the right
      // status_entrega lifecycle (null → sent/failed → delivered → read via webhook).
      if (dados.canal === 'whatsapp') {
        const result = await sendWhatsAppMessage({
          clienteId: dados.cliente_potencial_id,
          body: dados.conteudo,
          replyToBspId: dados.reply_to_bsp_id ?? undefined,
        });
        return {
          id: result.mensagemId,
          cliente_potencial_id: dados.cliente_potencial_id,
        } as Mensagem;
      }
      // Default: internal message via direct insert (existing behavior).
      return await enviarMensagem({ dados, orgId: requireOrgId() });
    },
    // Bolha otimista: aparece já com status null (relógio "a enviar").
    onMutate: async (dados: MensagemInsert) => {
      const orgId = requireOrgId();
      const key = ['mensagens', orgId, dados.cliente_potencial_id];
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<Mensagem[]>(key);
      const optimistic = buildOptimisticMensagem(
        dados,
        orgId,
        `optimistic-${crypto.randomUUID()}`,
        new Date().toISOString(),
      );
      queryClient.setQueryData<Mensagem[]>(key, (old) => [...(old ?? []), optimistic]);
      return { key, previous };
    },
    onError: (error: Error, _dados, context) => {
      // Rollback da bolha otimista para o snapshot anterior.
      if (context?.previous !== undefined) {
        queryClient.setQueryData(context.key, context.previous);
      }
      captureError(error, { extra: { hook: 'useEnviarMensagem' } });
      console.error('Erro ao enviar mensagem:', error);
    },
    onSettled: () => {
      // Verdade do servidor substitui a bolha otimista (a query é refeita por
      // inteiro, por isso o id 'optimistic-…' desaparece sem dedupe).
      queryClient.invalidateQueries({ queryKey: ['mensagens'] });
      queryClient.invalidateQueries({ queryKey: ['cliente'] });
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
    },
  });
}

export interface ReagirInput {
  clienteId: string;
  mensagemId: string;
  bspMessageId: string;
  emoji: string; // '' remove
}

export function useReagir() {
  const queryClient = useQueryClient();
  const { requireOrgId } = useOrganization();
  return useMutation({
    mutationFn: (input: ReagirInput) =>
      sendReaction({ clienteId: input.clienteId, bspMessageId: input.bspMessageId, emoji: input.emoji }),
    onMutate: async (input: ReagirInput) => {
      const key = ['mensagens', requireOrgId(), input.clienteId];
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<Mensagem[]>(key);
      queryClient.setQueryData<Mensagem[]>(key, (old) =>
        (old ?? []).map((m) =>
          m.id === input.mensagemId ? { ...m, reacao: input.emoji || null } : m,
        ),
      );
      return { key, previous };
    },
    onError: (_e: unknown, _input: ReagirInput, context: { key: string[]; previous: Mensagem[] | undefined } | undefined) => {
      if (context?.previous !== undefined) queryClient.setQueryData(context.key, context.previous);
    },
    onSettled: (_d: unknown, _e: unknown, input: ReagirInput) => {
      queryClient.invalidateQueries({ queryKey: ['mensagens', requireOrgId(), input.clienteId] });
    },
  });
}

export interface SendMidiaProntaParams {
  clienteId: string;
  storagePath: string;
  mimeType: string;
  filename: string;
  caption?: string;
  tipoMedia: 'image' | 'document' | 'audio';
}

/**
 * Faz upload do ficheiro ao bucket (sem enviar). Chamado ao abrir o preview,
 * para o ficheiro estar pronto antes do "Enviar" — o envio fica instantâneo.
 */
export function useUploadMidia() {
  const { requireOrgId } = useOrganization();
  return useMutation({
    mutationFn: (file: File) => uploadOutboundMedia(requireOrgId(), file),
    onError: (error: Error) => {
      captureError(error, { extra: { hook: 'useUploadMidia' } });
      console.error('Erro ao carregar mídia:', error);
    },
  });
}

/**
 * Envia uma media JÁ carregada no bucket via edge fn send-whatsapp. Invalida
 * mensagens, inbox e lista de clientes.
 */
export function useSendMidia() {
  const queryClient = useQueryClient();
  const { requireOrgId } = useOrganization();
  return useMutation({
    mutationFn: (params: SendMidiaProntaParams) => sendWhatsAppMedia(params),
    onMutate: async (params: SendMidiaProntaParams) => {
      const key = ['mensagens', requireOrgId(), params.clienteId];
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<Mensagem[]>(key);
      const optimistic = buildOptimisticMidia(
        params,
        requireOrgId(),
        `optimistic-${crypto.randomUUID()}`,
        new Date().toISOString(),
      );
      queryClient.setQueryData<Mensagem[]>(key, (old) => [...(old ?? []), optimistic]);
      return { key, previous };
    },
    onError: (error: Error, _params, context) => {
      if (context?.previous !== undefined) queryClient.setQueryData(context.key, context.previous);
      captureError(error, { extra: { hook: 'useSendMidia' } });
      console.error('Erro ao enviar mídia:', error);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['mensagens'] });
      queryClient.invalidateQueries({ queryKey: ['inbox-previews'] });
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
    },
  });
}

/**
 * Inicia uma conversa WhatsApp (cria/encontra cliente + envia template de
 * abertura). Invalida a lista de clientes. O componente trata da navegação
 * e dos toasts a partir do resultado.
 */
export function useIniciarConversa() {
  const queryClient = useQueryClient();
  return useMutation<IniciarConversaResult, Error, IniciarConversaParams>({
    mutationFn: (params) => iniciarConversa(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
    },
  });
}
